// ============================================================
// App.js
// Admin portal root. Public route (LoginPage) + authenticated
// routes wrapped in the persistent <AppShell> (SideNav + main
// content + Toaster).
//
// Authenticated landing redirects to /tenants (Workstream 2
// design decision: "Landing page is /tenants. No dashboard
// tiles in v1.") HomePage was removed in the Foundation pass
// — the tile-based landing was a Layer-1 placeholder.
//
// Coming-soon stubs render at routes whose page hasn't landed
// yet (Users list lands in a later session). They live inside
// AppShell so SideNav and Toaster behaviour is consistent.
// ============================================================

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import LoginPage from './pages/LoginPage';
import InvitationsPage from './pages/InvitationsPage';
import TenantsPage from './pages/TenantsPage';
import TenantDetailPage from './pages/TenantDetailPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import UsersPage from './pages/UsersPage';
import UserDetailPage from './pages/UserDetailPage';
import TrialRequestsPage from './pages/TrialRequestsPage';
import ContactMessagesPage from './pages/ContactMessagesPage';
import TrialRequestDetailPage from './pages/TrialRequestDetailPage';
import NewInvitationPage from './pages/NewInvitationPage';
import ComingSoonPage from './pages/ComingSoonPage';
import PlatformBillingPage from './pages/PlatformBillingPage';
import PlatformBillingSummaryPage from './pages/PlatformBillingSummaryPage';
import PlatformEventTypesPage from './pages/PlatformEventTypesPage';
import PlatformTrainingTypesPage from './pages/PlatformTrainingTypesPage';
import PlatformAuditTemplatesPage from './pages/PlatformAuditTemplatesPage';
import PlatformNotificationTypesPage from './pages/PlatformNotificationTypesPage';
import OperatorNotificationsPage from './pages/OperatorNotificationsPage';
import ServiceNotificationsPage from './pages/ServiceNotificationsPage';
import NotificationLogPage from './pages/NotificationLogPage';
import DeletionRequestsPage from './pages/DeletionRequestsPage';
import PlatformTenantIntegrationsPage from './pages/PlatformTenantIntegrationsPage';
import PlatformAnalyticsPage from './pages/PlatformAnalyticsPage';
import ProtectedRoute from './components/ProtectedRoute';
import AppShell from './components/AppShell';
import LegalFooter from './components/LegalFooter';
import LiveAnnouncer from './components/a11y/LiveAnnouncer';
import './App.css';

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  const isStartingUp =
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect;

  return (
    <>
      {/* Mounted ABOVE the Routes so the live regions survive every
          navigation — a region that unmounts loses exactly the property
          that makes it announce reliably (see LiveAnnouncer.jsx). */}
      <LiveAnnouncer />
      {isStartingUp ? (
        <LoadingScreen message="Starting up…" />
      ) : (
        <Routes>
          <Route
            path="/"
            /* The legal footer stays on the SIGN-IN screen and nowhere
               else. Those links are customer-facing publications, and
               everyone past this point is A2Z IT staff on a UPN
               allowlist — telling our own operators how we process
               customer data serves nobody, and on a phone it is a
               permanent third row under every list. The sign-in screen
               is the one surface a non-A2Z visitor can reach, so the
               link keeps its only real audience. */
            element={isAuthenticated
              ? <Navigate to="/tenants" replace />
              : <><LoginPage /><LegalFooter /></>}
          />

          {/* Authenticated routes share the persistent shell. */}
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/tenants" element={<TenantsPage />} />
            <Route path="/tenants/:tenantId" element={<TenantDetailPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:clientId" element={<ClientDetailPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/users/:clientUserId" element={<UserDetailPage />} />
            <Route path="/invitations" element={<InvitationsPage />} />
            <Route path="/invitations/new" element={<NewInvitationPage />} />
            <Route path="/trial-requests" element={<TrialRequestsPage />} />
            <Route path="/contact-messages" element={<ContactMessagesPage />} />
            <Route path="/trial-requests/:trialRequestId" element={<TrialRequestDetailPage />} />
            <Route path="/platform/billing"            element={<PlatformBillingPage />} />
            <Route path="/platform/billing-summary"    element={<PlatformBillingSummaryPage />} />
            <Route path="/platform/event-types"        element={<PlatformEventTypesPage />} />
            <Route path="/platform/training-types"     element={<PlatformTrainingTypesPage />} />
            <Route path="/platform/audit-templates"    element={<PlatformAuditTemplatesPage />} />
            <Route path="/platform/notification-types" element={<PlatformNotificationTypesPage />} />
            <Route path="/platform/operator-notifications" element={<OperatorNotificationsPage />} />
            <Route path="/platform/service-notifications" element={<ServiceNotificationsPage />} />
            <Route path="/platform/notification-log"   element={<NotificationLogPage />} />
            <Route path="/platform/deletion-requests"  element={<DeletionRequestsPage />} />
            <Route path="/platform/tenant-integrations" element={<PlatformTenantIntegrationsPage />} />
            <Route path="/platform/analytics"          element={<PlatformAnalyticsPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </>
  );
}

function LoadingScreen({ message }) {
  return (
    <div className="loading-screen">
      <div className="loading-screen__content">
        <div className="loading-screen__logo">
          <span className="logo-a">A2Z</span>
          <span className="logo-care">Care</span>
          <span className="logo-hub">Hub</span>
        </div>
        <div className="loading-screen__dots">
          <span /><span /><span />
        </div>
        <p>{message}</p>
      </div>
    </div>
  );
}
